/**
 * INC-329 — where "Remind me later" is remembered.
 *
 * ## The decision, and why it had to be made here
 *
 * Under the chosen option (b) the Flagged predicate is read from the profile endpoint on
 * EVERY app init, not once from the login response. That exposes a question option (a)
 * hid: `settledKeys` lived only in memory, so it survived navigation but not a page
 * refresh. Left alone, a flagged investor who refreshes would be re-nudged on every single
 * page load — nagging, not a compliance control.
 *
 * `sessionStorage` is the answer, and deliberately not `localStorage`:
 *
 * - It matches the acceptance criterion word for word — "puts the item off FOR THE
 *   SESSION, and the item returns on the NEXT LOGIN". A session-storage entry dies with
 *   the tab, so the nudge comes back exactly when the AC says it should.
 * - `localStorage` would outlive logins. A dismissal made once would quietly suppress a
 *   compliance nudge for good, which is the opposite of what this gate is for.
 * - Server-side "snoozed until" state is the honest long-term answer, but it needs a
 *   product and compliance decision plus an API. This is the reversible option: delete
 *   this file and pass no store to {@link InterruptQueue} and the behaviour is back to
 *   in-memory, with nothing else to unpick.
 *
 * The tab is not the only boundary — {@link SettledStore.clear} is called at BOTH ends of a
 * session: on every successful login (`PostLoginInterruptService.startSession`) and when
 * `AppComponent` first sees an unauthenticated state (`reset`). So logging out and back in within
 * one tab re-raises the item as the AC requires, and nothing a previous user dismissed can survive
 * into the next session.
 *
 * The key is deliberately NOT scoped by user id. It was considered during review: with both ends of
 * the session clearing it, scoping would only guard a hypothetical future auth path that bypasses
 * both, at the cost of plumbing a user id into a class that otherwise needs no notion of one. If
 * such a path is ever added, scope this key rather than relying on the convention.
 */
export interface SettledStore {
  load(): string[];
  save(keys: string[]): void;
  clear(): void;
}

export const SETTLED_STORAGE_KEY = 'pitchin.postLoginInterrupts.settled';

/**
 * A store that keeps nothing — the server-side-rendering case, and the fallback when the
 * browser refuses storage (Safari private mode throws on access rather than returning
 * null). Losing a dismissal degrades to "shown again", never to "gate skipped", so failing
 * this way round is safe.
 */
export const NULL_SETTLED_STORE: SettledStore = {
  load: () => [],
  save: () => {},
  clear: () => {},
};

/** Session-scoped store over any `Storage`. Every access is guarded — see above. */
export class SessionSettledStore implements SettledStore {
  constructor(
    private readonly storage: Storage,
    private readonly storageKey: string = SETTLED_STORAGE_KEY,
  ) {}

  load(): string[] {
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      // Anything but an array of strings is treated as absent — a corrupted or
      // hand-edited entry must not be able to suppress an interrupt.
      return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
    } catch {
      return [];
    }
  }

  save(keys: string[]): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(keys));
    } catch {
      // Quota or a blocked storage API. The dismissal stays in memory for this page view.
    }
  }

  clear(): void {
    try {
      this.storage.removeItem(this.storageKey);
    } catch {
      // As above — nothing to do, and nothing unsafe about failing to forget.
    }
  }
}

/**
 * Picks the real store in a browser and the no-op one everywhere else (SSR, or storage
 * blocked). Kept as a plain function so nothing here needs Angular.
 */
export function createSettledStore(): SettledStore {
  try {
    if (typeof sessionStorage === 'undefined' || sessionStorage === null) {
      return NULL_SETTLED_STORE;
    }
    // Touch it once: some browsers only throw on first use, and we would rather find out
    // now than half way through a settle.
    sessionStorage.getItem(SETTLED_STORAGE_KEY);
    return new SessionSettledStore(sessionStorage);
  } catch {
    return NULL_SETTLED_STORE;
  }
}
